import { IQuery, buildUrl } from '../../services/BaseServiceV2';

import AccountsService from '../AccountsService';
import { AuthProvider } from './Types';
import Environment from '../../Environment';
import GetAppHeaders from '../../Downloader/GetAppHeaders';
import { Platform } from 'react-native';
import { SUBSPLASH_AUTH_PROVIDER_ID } from '../../constants/identifiers';
import TokensService from '@omni/kit/services/TokensService';

interface Props {
  appKey?: string;
  orgKey?: string;
}

const debug = require('debug')('tca:auth:utilities');

export const getTokenUrl = async (
  appKey?: string,
  authProviderId?: string
): Promise<string | undefined> => {
  if (!appKey) {
    return undefined;
  }

  let tokenUrl: string | undefined;
  try {
    const app = await AccountsService.getApp(appKey);
    const orgKey = app.org_key;

    const providers = await fetchAuthProviders({ appKey, orgKey });
    const targetAuthProviderId = authProviderId ?? SUBSPLASH_AUTH_PROVIDER_ID;

    const targetAPC = providers.find(
      (provider) => provider.authproviderid === targetAuthProviderId
    );
    tokenUrl = targetAPC?.token_url;
  } catch {}

  return tokenUrl;
};

export const fetchAuthProviders = async ({
  appKey,
  orgKey,
}: Props): Promise<AuthProvider[]> => {
  if (!appKey && !orgKey) {
    return [];
  }

  try {
    const queries: IQuery[] = [];

    if (orgKey && Platform.OS === 'web') {
      queries.push(['org_key', orgKey]);
    } else {
      queries.push(['app_key', appKey]);
    }

    // required for backend filtering + sorting in go/feeds
    const appHeaders = await GetAppHeaders(appKey);

    /**
     * We must use 'fetch' here instead of 'FeedsService.get' to avoid
     * recursive dependency on content_domain validation.
     * This is just a one-off special case. Normally requests should use
     * either BaseService or downloadUrl for requests in order to
     * leverage logic around request headers, domain validation and caching.
     */
    const url = buildUrl({
      host: Environment.feedsService,
      resource: 'auth-providers',
      queries,
    });

    const guestToken = await TokensService.getGuestToken();

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        ...appHeaders,
        Authorization: 'Bearer ' + guestToken,
      },
    });

    if (response.ok) {
      try {
        const responseBody = (await response.json()) || [];
        return responseBody as AuthProvider[];
      } catch (error) {
        debug(`Error parsing auth providers: ${error}`);
      }
    }
  } catch (error) {
    debug(`Error fetching auth providers:${error}`);
  }

  return [];
};

export const isUrlAuthorized = async ({
  appKey,
  authProviderId,
  orgKey,
  url,
}: {
  appKey?: string;
  authProviderId?: string;
  orgKey?: string;
  url: string;
}): Promise<boolean> => {
  if (!authProviderId) {
    debug('Missing required authProviderId');

    return false;
  }

  /**
   * Special case to use hardcoded domain whitelist for the subsplash provider.
   * There are several benefits to this:
   *
   * 1. Optimization: Allows content to load faster that relies on subsplash domains
   * since we can skip the network call that obtains the subsplash provider content_domain.
   *
   * 2. Security: The backend does not yet provide any content_domain for
   * the subsplash provider. Providing a hardcoded set within the project is
   * a big security improvement while we wait on backend to provide content_domain.
   *
   * 3. Convenience: Callers should not have to pass through an appKey and orgKey
   * prop if we are making request using subsplash domains.
   * e.g. see usage of 'downloadUrl' in kit/hooks/useMediaStatus.ts
   */
  if (authProviderId === SUBSPLASH_AUTH_PROVIDER_ID) {
    return isUrlAuthorizedForSubsplashDomains(url);
  }

  if (!appKey && !orgKey) {
    debug('Missing appKey or orgKey');

    return false;
  }

  try {
    const providers = await fetchAuthProviders({ appKey, orgKey });

    return isUrlAuthorizedForProviders({ authProviderId, providers, url });
  } catch {}

  return false;
};

export const isUrlAuthorizedForProviders = ({
  authProviderId,
  providers,
  url,
}: {
  authProviderId: string;
  providers: AuthProvider[];
  url: string;
}): boolean => {
  const targetProvider = providers?.find(
    (provider) => provider.authproviderid === authProviderId
  );

  if (targetProvider?.content_domain) {
    const authorized = isUrlAuthorizedForContentDomain(
      url,
      targetProvider.content_domain
    );

    return authorized;
  } else {
    /**
     * TODO: when backend is ready, change to return 'false' here
     * when provider is missing content_domain field.
     */
    return true;
  }
};

export const getAuthorizedProvider = ({
  providers,
  url,
}: {
  providers: AuthProvider[];
  url: string;
}): AuthProvider | undefined => {
  const authorizedProvider = providers?.find(
    (provider) =>
      provider.content_domain &&
      isUrlAuthorizedForContentDomain(url, provider.content_domain)
  );

  return authorizedProvider;
};

/**
 * Check to see if the host ends with a valid domain
 * e.g. host = secure.subsplash.com, authorizedDomain = subsplash.com
 */
export const isUrlAuthorizedForContentDomain = (
  url: string,
  content_domain: string[]
): boolean => {
  let authorized = false;

  const host = getHostName(url);

  if (host) {
    const match = content_domain.find((authorizedSuffix) =>
      host.endsWith(authorizedSuffix)
    );

    authorized = Boolean(match);
  }

  return authorized;
};

const getHostName = (url: string): string | undefined => {
  let hostName: string | undefined;
  try {
    /**
     * Note that parsing the url with `new URL` may fail in react-native
     * on certain urls by default. A polyfill is required for this to work
     * as expected on iOS and Android.
     * The native index.js must import 'react-native-url-polyfill/auto'.
     * ref: https://github.com/facebook/react-native/issues/23922#issuecomment-648096619
     * ref: https://www.npmjs.com/package/react-native-url-polyfill
     */
    const { host } = new URL(url);
    hostName = host;
  } catch (error) {
    debug(`Unable to resolve domain: ${error}`);
  }

  return hostName;
};

/*
 * TODO: Ideally, the app should authorize content domains
 * using auth provider data from the feeds service, but this is intended to improve
 * security without invasive changes or high regression risk for the 5.17 release.
 * Since 'fetchWithAuth' is used around requests for Subsplash services,
 * this should be safe without causing a regression
 */
export const isUrlAuthorizedForSubsplashDomains = (url: string): boolean => {
  const subsplashContentDomains = ['subsplash.com', 'subsplash.net'];

  return isUrlAuthorizedForContentDomain(url, subsplashContentDomains);
};
